import { ReactNode, useId } from "react";
import { Box, BoxProps, Button, ButtonProps, TextField, TextFieldProps } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";

// The look of the builder's pages, taken from the approved mock: cards, headings, fields and buttons.

export function BuilderCard({ sx, ...props }: BoxProps) {
  return (
    <Box
      {...props}
      sx={[
        { bgcolor: "#ffffff", border: "1px solid #e4e7ec", borderRadius: "10px", p: "18px", boxSizing: "border-box", display: "flex", flexDirection: "column", fontSize: 13, lineHeight: "normal", color: "#1d2939" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}

export function CardHeading({ children }: { children: ReactNode }) {
  return (
    <Box component="h2" sx={{ m: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#667085", textTransform: "uppercase" }}>
      {children}
    </Box>
  );
}

export function PageTitle({ children }: { children: ReactNode }) {
  return <Box component="h1" sx={{ m: 0, fontSize: 22, fontWeight: 600, color: "#1d2939" }}>{children}</Box>;
}

function FieldLabel({ id, htmlFor, children }: { id?: string; htmlFor?: string; children: ReactNode }) {
  return <Box component="label" id={id} htmlFor={htmlFor} sx={{ fontSize: 12, color: "#475467" }}>{children}</Box>;
}

/** A field without a label of its own: the caller puts one beside it or above it. */
export function BuilderInput({ sx, ...props }: TextFieldProps) {
  return (
    <TextField
      size="small"
      fullWidth
      {...props}
      sx={[
        {
          "& .MuiOutlinedInput-root": { height: 40, borderRadius: "8px", fontSize: 13, color: "#1d2939", bgcolor: "#ffffff" },
          "& .MuiOutlinedInput-root:not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d5dd" },
          "& .MuiOutlinedInput-input": { pl: "10px" },
          "& .MuiFormHelperText-root": { mx: 0 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}

/** The label sits above the field, as in the mock. */
export function BuilderTextField({ label, slotProps, ...props }: Omit<TextFieldProps, "label"> & { label: string }) {
  const id = useId();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
      <FieldLabel id={`${id}-label`} htmlFor={id}>{label}</FieldLabel>
      <BuilderInput id={id} {...props} slotProps={{ ...slotProps, select: { labelId: `${id}-label` } }} />
    </Box>
  );
}

/** A day picked from a calendar or typed; the value is YYYY-MM-DD, or "" while unset or half typed. */
export function BuilderDateField({ label, value, onChange, error, helperText }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: ReactNode;
}) {
  const id = useId();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(day) => onChange(day?.isValid() ? day.format("YYYY-MM-DD") : "")}
          enableAccessibleFieldDOMStructure={false}
          slots={{ textField: BuilderInput }}
          slotProps={{ textField: { id, error, helperText } }}
        />
      </LocalizationProvider>
    </Box>
  );
}

export function PrimaryButton({ sx, ...props }: ButtonProps) {
  return (
    <Button
      variant="contained"
      disableElevation
      {...props}
      sx={[
        { height: 44, px: "22px", borderRadius: "8px", fontSize: 13, fontWeight: 600, letterSpacing: "0.04em" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
